import React, { memo, useCallback, useEffect, useState } from 'react';
import { InnerContainer } from '@/components/atoms/Containers';
import Grid from '@/components/molecules/Grid';
import { useTranslation } from 'react-i18next';
import { TestH1 } from '@/pages/designSystem/Fonts';
import Icon from '@/components/atoms/UI/Icon/Icon';
import Button from '@/components/atoms/UI/buttons/Button';
import { ResultHeaderWrap } from './styles';
import { basicColor, testBackground } from '@/styles/template';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumbs from '@/components/molecules/Breadcrumbs/Breadcrumbs';
import StickyContainer from '@/components/templates/containers/StickyContainer';
import { selectCurrentRating, selectRatingPercent } from '@/store/selectors/ratingSelectors';
import StarRating from '@/components/features/StarRating/StarRating';
import HistoryModal from '@/components/features/result/HistoryModal/HistoryModal';
import { selectBreadcrumbItems } from '@/store/selectors/breadcrumbSelector';
import {
    selectBlocks,
    selectResultBackground,
    selectResultTestId, selectResultTestUrl,
    selectTestTitle
} from '@/store/selectors/resultSelectors';
import SmartCloseBackButton from '@/components/molecules/SmartCloseBackButton/SmartCloseBackButton';
import Testing from '@/pages/testing/Testing';
import { resetResult } from '@/store/reducers/resultSlice';
import { AppDispatch } from '@/store/store';
import ConditionalBlock from '@/components/features/result/ConditionalBlock/ConditionalBlock';
import ThemeSwitcher from '@/components/molecules/ThemeSwitcher';
import { selectHistory, selectHistoryItems } from '@/store/selectors/resultHistorySelector';
import { selectTestBackground } from '@/store/selectors/selectTestBackground';
import { useBackgroundColor } from '@/core/hooks/useBackgroundColor';
import { fetchHistoryResultData } from '@/store/reducers/historyResultSlice';
import { resetTestingState, setIsTesting } from '@/store/reducers/testingSlice';
import { useNavigate } from 'react-router-dom';


interface ResultHeaderProps {
    triggerRef?: any;
    fromAttempt?: boolean;
    clickedBtn?: string;
}

const ResultHeader: React.FC<ResultHeaderProps> = ({ triggerRef, fromAttempt, clickedBtn }) => {
    const dispatch: AppDispatch = useDispatch();

    const resultBackground = useSelector(selectResultBackground);
    const backgroundColor = testBackground[resultBackground] || basicColor.primary
    const testId = useSelector(selectResultTestId);
    const testUrl = useSelector(selectResultTestUrl);
    const breadcrumbItems = useSelector(selectBreadcrumbItems);
    const historyItems = useSelector(selectHistoryItems);
    const currentRating = useSelector(selectCurrentRating);
    const ratingPercent = useSelector(selectRatingPercent);
    const testTitle = useSelector(selectTestTitle);
    const blocks = useSelector(selectBlocks);
    const resultBreadcrumb = blocks.resultBreadcrumb;
    const resultHistoryBlock = blocks.resultHistory;
    const backBtn = blocks.resultBtnListTests;
    const ratingBlock = blocks.resultRating;

    const { t } = useTranslation();
    const text_try_again = t('result.try_again' as any);
    const text_to_list = t('result.to_list' as any);
    const navigate = useNavigate();
    const [testingMode, setTestingMode] = useState(false);

    const RepeatHandler = () => {
        if (fromAttempt) {
            const cleanUrl = new URL(testUrl, window.location.origin);
            navigate(cleanUrl.pathname, {
                replace: true,
                state: {
                    clickedBtn: 'clearAndStart',
                    fromAttempt: true,
                    forceStart: true,
                }
            });

        } else {
            if (typeof window !== "undefined" && (window as any).renderTest) {
                (window as any).renderTest('clearAndStart');
            }
            else if (clickedBtn !== 'lastAttempt') {
                setTestingMode(true);
            }

        }
    };

    useEffect(() => {
        if (testId) {
            dispatch(fetchHistoryResultData(testId));
        }
    }, [dispatch, historyItems.length, testId]);

    if (testingMode) {
        return <Testing clickedBtn={'clearAndStart'} />
    }


    return (
        <ResultHeaderWrap style={{ background: backgroundColor }}>
            <ConditionalBlock block={backBtn}>
                <StickyContainer triggerRef={triggerRef}>
                    <SmartCloseBackButton>
                        <Button type='button' transparent autoWidth small>
                            <Icon name='Back' xSmall />
                            {text_to_list}
                        </Button>
                    </SmartCloseBackButton>
                </StickyContainer>
            </ConditionalBlock>

            <InnerContainer>
                <Grid col='1' gap='2' gapDesktop='3' margin='4'  centeredContent centeredText>
                    <ConditionalBlock block={resultBreadcrumb}>
                        <Breadcrumbs items={breadcrumbItems} />
                    </ConditionalBlock>

                    <TestH1>{testTitle}</TestH1>

                    <ConditionalBlock block={ratingBlock}>
                        <StarRating rating={currentRating} ratingPercent={ratingPercent} >{currentRating}</StarRating>
                    </ConditionalBlock>

                    <Button transparent autoWidth onClick={RepeatHandler}>
                        <Icon name='Repeat' />
                        {text_try_again}
                    </Button>

                    <ConditionalBlock block={resultHistoryBlock}>
                        {(historyItems.length > 1) && <HistoryModal/>}
                    </ConditionalBlock>

                    {/*<ThemeSwitcher />*/}
                </Grid>
            </InnerContainer>
        </ResultHeaderWrap>
    );
};

export default memo(ResultHeader);
